import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { IndianRupee, CheckCircle, Clock, Scale, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

function fmt(n) { return n ? '₹' + Number(n).toLocaleString('en-IN') : '₹0'; }

// ---- Payments Page ----
export function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const canVerify = ['admin', 'tl'].includes(user?.role);

  const load = () => {
    Promise.all([
      api.get('/payments').then(r => setPayments(r.data)),
      api.get('/payments/summary').then(r => setSummary(r.data)).catch(() => {})
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const verify = async (paymentId) => {
    try {
      await api.patch(`/payments/${paymentId}/verify`);
      toast.success('Payment verified — timeline updated!');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to verify');
    }
  };

  const typeColor = { enrollment: 'badge-green', retainer: 'badge-blue', analysis_fee: 'badge-purple', manual: 'badge-gray' };

  // Pending that this user can verify
  const myPendingCount = payments.filter(p => !p.verified && (
    (p.approvalLevel === 'tl' && ['admin','tl'].includes(user?.role)) ||
    (p.approvalLevel === 'admin' && user?.role === 'admin')
  )).length;

  return (
    <div className="page">
      <div className="flex-between mb-24">
        <h2 style={{ fontSize: 18 }}>Payments & Finance</h2>
      </div>

      {canVerify && myPendingCount > 0 && (
        <div style={{ padding: '12px 16px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertCircle size={18} style={{ color: 'var(--warning)', flexShrink: 0 }} />
          <div style={{ flex: 1, fontSize: 13 }}>
            <strong>{myPendingCount} payment{myPendingCount > 1 ? 's' : ''}</strong> pending your verification — these will not count toward targets until verified.
          </div>
        </div>
      )}

      {/* Payment type filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { key: 'all', label: `All (${payments.length})` },
          { key: 'razorpay', label: `✅ Razorpay Verified (${payments.filter(p => p.mode === 'Razorpay' && p.verified).length})` },
          { key: 'manual_verified', label: `👤 Manual Approved (${payments.filter(p => p.mode !== 'Razorpay' && p.verified).length})` },
          { key: 'pending', label: `⏳ Pending (${payments.filter(p => !p.verified).length})` },
        ].map(t => (
          <button key={t.key} className={`btn btn-sm ${paymentFilter === t.key ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPaymentFilter(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {summary && (
        <div className="grid grid-4 mb-24">
          {[
            { label: "Today's Collection", value: fmt(summary.todayCollection), color: '#10b981' },
            { label: 'Monthly Collection', value: fmt(summary.monthCollection), color: '#3b82f6' },
            { label: 'Enrollment', value: fmt(summary.enrollmentCollection), color: '#8b5cf6' },
            { label: 'Retainer', value: fmt(summary.retainerCollection), color: '#f59e0b' },
          ].map(s => (
            <div key={s.label} className="stat-card" style={{ borderTop: `3px solid ${s.color}` }}>
              <div className="card-title">{s.label}</div>
              <div className="card-value">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : (() => {
          const filteredPayments = payments.filter(p => {
            if (paymentFilter === 'razorpay') return p.mode === 'Razorpay' && p.verified;
            if (paymentFilter === 'manual_verified') return p.mode !== 'Razorpay' && p.verified;
            if (paymentFilter === 'pending') return !p.verified;
            return true;
          });
          return (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Date</th><th>Type</th><th>Amount</th><th>Mode</th><th>By</th><th>UTR</th><th>Screenshot</th><th>Status</th><th>Approval Needed</th>{canVerify && <th>Action</th>}</tr>
              </thead>
              <tbody>
                {filteredPayments.map(p => (
                  <tr key={p.id} style={{ background: !p.verified ? '#fffbeb' : '' }}>
                    <td style={{ fontSize: 12 }}>{new Date(p.createdAt).toLocaleDateString('en-IN')}</td>
                    <td><span className={`badge ${typeColor[p.type] || 'badge-gray'}`}>{p.type}</span></td>
                    <td style={{ fontWeight: 700, color: 'var(--success)' }}>{fmt(p.amount)}</td>
                    <td style={{ fontSize: 12 }}>{p.mode}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.receivedByName || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.utr || '—'}</td>
                    <td>
                      {p.screenshotFile
                        ? <a href={`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/payments/screenshot/${p.screenshotFile}`} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}>📷 View</a>
                        : p.mode === 'Razorpay' ? <span style={{ fontSize: 11, color: 'var(--success)' }}>Auto</span>
                        : <span style={{ fontSize: 11, color: 'var(--danger)' }}>Missing</span>}
                    </td>
                    <td>
                      {p.verified
                        ? <span className="badge badge-green">✓ Verified</span>
                        : <span className="badge badge-yellow">⏳ Pending</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {!p.verified && p.approvalLevel && (
                        <span style={{ color: p.approvalLevel === 'admin' ? 'var(--danger)' : 'var(--warning)', fontWeight: 600, fontSize: 11 }}>
                          {p.approvalLevel === 'admin' ? '👑 Admin' : '👤 TL'}
                        </span>
                      )}
                    </td>
                    {canVerify && (
                      <td>
                        {!p.verified && (
                          (p.approvalLevel === 'admin' && user?.role === 'admin') ||
                          (p.approvalLevel === 'tl' && ['admin','tl'].includes(user?.role))
                        ) && (
                          <button className="btn btn-success btn-sm" onClick={() => verify(p.id)}>✓ Verify</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          );
        })()}
      </div>
    </div>
  );
}

// ---- Approvals Page ----
export function ApprovalsPage() {
  const [approvals, setApprovals] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const { user } = useAuth();

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/approvals', { params: { status: filter } }).then(r => setApprovals(r.data)),
      // Load pending payments needing approval
      filter === 'pending' ? api.get('/payments').then(r => {
        const pending = r.data.filter(p => !p.verified && p.verificationRequired);
        setPendingPayments(pending);
      }).catch(() => {}) : Promise.resolve()
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]);

  const resolve = async (id, status, remarks, approval) => {
    await api.patch(`/approvals/${id}`, { status, remarks });
    if (status === 'approved' && (approval?.type === 'client_revival_tl' || approval?.type === 'client_revival_admin')) {
      await api.post(`/leads/${approval.leadId}/complete-revival`);
      toast.success('Approval granted — Client revived!');
    } else {
      toast.success(`Approval ${status}`);
    }
    load();
  };

  const verifyPayment = async (paymentId) => {
    try {
      await api.patch(`/payments/${paymentId}/verify`);
      toast.success('Payment verified!');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    }
  };

  const canVerifyPayment = (p) => {
    if (p.approvalLevel === 'admin' && user?.role === 'admin') return true;
    if (p.approvalLevel === 'tl' && ['admin','tl'].includes(user?.role)) return true;
    return false;
  };

  return (
    <div className="page">
      <div className="flex-between mb-24">
        <h2 style={{ fontSize: 18 }}>Approvals</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {['pending','approved','rejected'].map(s => (
            <button key={s} className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter(s)} style={{ textTransform: 'capitalize' }}>{s}</button>
          ))}
        </div>
      </div>

      {/* Pending Payment Approvals */}
      {filter === 'pending' && pendingPayments.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            💰 Payment Verifications Pending ({pendingPayments.length})
          </div>
          {pendingPayments.map(p => (
            <div key={p.id} className="card mb-10" style={{ borderLeft: '4px solid var(--warning)' }}>
              <div className="flex-between">
                <div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                    <span className="badge badge-yellow">⏳ Pending Verification</span>
                    <span className="badge badge-blue">{p.type}</span>
                    <span style={{ fontWeight: 700, color: 'var(--success)', fontSize: 14 }}>{fmt(p.amount)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 16 }}>
                    <span>Mode: {p.mode}</span>
                    {p.utr && <span>UTR: {p.utr}</span>}
                    <span>By: {p.receivedByName || 'Unknown'}</span>
                    <span>{new Date(p.createdAt).toLocaleDateString('en-IN')}</span>
                    <span style={{ color: p.approvalLevel === 'admin' ? 'var(--danger)' : 'var(--warning)', fontWeight: 600 }}>
                      Needs: {p.approvalLevel === 'admin' ? '👑 Admin' : '👤 TL'}
                    </span>
                  </div>
                  {p.notes && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Note: {p.notes}</div>}
                </div>
                {canVerifyPayment(p) && (
                  <button className="btn btn-success btn-sm" onClick={() => verifyPayment(p.id)}>✓ Verify</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Other Approvals */}
      {approvals.length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            📋 Other Approvals
          </div>
          {approvals.map(a => (
            <div key={a.id} className="card mb-12">
              <div className="flex-between">
                <div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                    <span className={`badge ${a.status === 'pending' ? 'badge-yellow' : a.status === 'approved' ? 'badge-green' : 'badge-red'}`}>{a.status}</span>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{a.type?.replace(/_/g, ' ')}</span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>{a.description}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-light)' }}>By {a.requestedByName} · {new Date(a.createdAt).toLocaleDateString('en-IN')}</div>
                </div>
                {a.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-success btn-sm" onClick={() => resolve(a.id, 'approved', 'Approved', a)}>✓ Approve</button>
                    <button className="btn btn-danger btn-sm" onClick={() => resolve(a.id, 'rejected', 'Rejected', a)}>✗ Reject</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>}
      {!loading && approvals.length === 0 && pendingPayments.length === 0 && (
        <div className="empty-state"><Scale size={40} /><h3>No {filter} approvals</h3></div>
      )}
    </div>
  );
}

// ---- Follow-ups Page ----
export function FollowUpsPage() {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get all follow-ups from leads
    api.get('/leads').then(r => {
      const leads = r.data;
      const today = new Date().toISOString().split('T')[0];
      const fus = [];
      leads.forEach(l => {
        if (l.nextFollowUp) {
          fus.push({ leadName: l.name, mobile: l.mobile, date: l.nextFollowUp.split('T')[0], stage: l.stage, leadId: l.id });
        }
        if (l.promiseDate) {
          fus.push({ leadName: l.name, mobile: l.mobile, date: l.promiseDate, type: 'promise', amount: l.promiseAmount, leadId: l.id });
        }
      });
      setFollowUps(fus.sort((a, b) => a.date.localeCompare(b.date)));
    }).finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().split('T')[0];
  const todayFus = followUps.filter(f => f.date === today);
  const overdue = followUps.filter(f => f.date < today);
  const upcoming = followUps.filter(f => f.date > today);

  const Section = ({ title, items, color }) => items.length > 0 && (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 14, marginBottom: 12, color }}>{title} ({items.length})</h3>
      {items.map((f, i) => (
        <div key={i} className="card mb-8" style={{ padding: '12px 16px', borderLeft: `3px solid ${color}` }}>
          <div className="flex-between">
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{f.leadName}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{f.mobile} · {f.stage || (f.type === 'promise' ? `Promise: ₹${f.amount?.toLocaleString('en-IN')}` : '')}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{f.date}</div>
              {f.type === 'promise' && <span className="badge badge-blue" style={{ marginTop: 4 }}>Promise</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="page">
      <h2 style={{ fontSize: 18, marginBottom: 24 }}>Follow-ups & Promises</h2>
      {loading ? <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div> : (
        <>
          <Section title="⚠️ Overdue" items={overdue} color="var(--danger)" />
          <Section title="📅 Today" items={todayFus} color="var(--warning)" />
          <Section title="📆 Upcoming" items={upcoming} color="var(--accent)" />
          {followUps.length === 0 && <div className="empty-state"><Clock size={40} /><h3>No follow-ups</h3><p>Schedule follow-ups from lead detail pages</p></div>}
        </>
      )}
    </div>
  );
}

// ---- Reports Page ----
// ---- Settings Page ----
export function SettingsPage() {
  return (
    <div className="page">
      <h2 style={{ fontSize: 18, marginBottom: 24 }}>Settings</h2>
      <div className="grid grid-2">
        {[
          { title: 'Company Settings', desc: 'Logo, address, GST, CIN, email for each company', icon: '🏢' },
          { title: 'User Management', desc: 'Add/edit users, assign roles, set monthly targets', icon: '👤' },
          { title: 'API Integrations', desc: 'Razorpay, Tata Tele, Zoho Sign, SMTP settings', icon: '🔌' },
          { title: 'AI & Prompt Settings', desc: 'Tune AI analysis prompts, proposal templates', icon: '🧠' },
          { title: 'Fee & Policy Settings', desc: 'Minimum fee, tenure limits, late fee rules', icon: '📋' },
          { title: 'WhatsApp Templates', desc: 'Edit and manage all WhatsApp message templates', icon: '💬' },
        ].map(s => (
          <div key={s.title} className="card" style={{ cursor: 'pointer' }} onClick={() => toast.success('Settings panel coming in Phase 2!')}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>{s.icon}</div>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{s.title}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{s.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Legal Page ----
// LegalPage moved to pages/LegalPage.js
